<script lang="ts">
  import { Button, Input, Textarea, Card, Collapse } from '@aatmanova/ui';

  interface Props {
    addToast: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
  }

  let { addToast }: Props = $props();

  // CMS configuration local states
  let heroHeading = $state(localStorage.getItem('aatmanova_cms_hero_heading') || 'Ancient Soul, Modern Gear');
  let heroSubheading = $state(localStorage.getItem('aatmanova_cms_hero_subheading') || 'Elevate your daily energy flow with curated wellness products and custom spiritual tools designed to resonate with you.');
  let heroCtaText = $state(localStorage.getItem('aatmanova_cms_hero_cta') || 'Shop Collection');

  let faqs = $state<Array<{ question: string; answer: string }>>(
    JSON.parse(localStorage.getItem('aatmanova_cms_faqs') || '[]').length > 0
      ? JSON.parse(localStorage.getItem('aatmanova_cms_faqs') || '[]')
      : [
          { question: 'What is a 7-Mukhi Rudraksha?', answer: 'It is a sacred bead representing the goddess Mahalakshmi, believed to bring wealth and prosperity.' },
          { question: 'Are the crystals authentic?', answer: 'Yes, all our crystals are hand-selected, verified for quality, and ethically sourced.' }
        ]
  );

  function saveHeroConfig() {
    localStorage.setItem('aatmanova_cms_hero_heading', heroHeading);
    localStorage.setItem('aatmanova_cms_hero_subheading', heroSubheading);
    localStorage.setItem('aatmanova_cms_hero_cta', heroCtaText);
    addToast('Hero configurations updated.', 'success');
  }

  function addFaq() {
    faqs = [...faqs, { question: 'New Question', answer: 'New Answer' }];
  }

  function removeFaq(index: number) {
    faqs = faqs.filter((_, i) => i !== index);
  }

  function saveFaqConfig() {
    localStorage.setItem('aatmanova_cms_faqs', JSON.stringify(faqs));
    addToast('FAQ accordion items updated.', 'success');
  }
</script>

<div class="space-y-6">
  <div class="space-y-1">
    <h2 class="text-3xl font-extrabold tracking-tight text-base-content">Storefront CMS</h2>
    <p class="text-sm text-base-content/70">Configure Landing Page Hero parameters and FAQ lists</p>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
    <!-- Hero Form -->
    <Card title="Hero Segment Content">
      <div class="space-y-4">
        <div class="form-control w-full">
          <label for="headingTitle" class="label">
            <span class="label-text text-xs font-semibold text-base-content/60 uppercase tracking-wider">Heading Title</span>
          </label>
          <Input
            type="text"
            id="headingTitle"
            bind:value={heroHeading}
            size="sm"
          />
        </div>

        <div class="form-control w-full">
          <label for="subheadingDesc" class="label">
            <span class="label-text text-xs font-semibold text-base-content/60 uppercase tracking-wider">Subheading Description</span>
          </label>
          <Textarea
            id="subheadingDesc"
            rows={3}
            bind:value={heroSubheading}
          />
        </div>

        <div class="form-control w-full">
          <label for="ctaLabel" class="label">
            <span class="label-text text-xs font-semibold text-base-content/60 uppercase tracking-wider">CTA Button Label</span>
          </label>
          <Input
            type="text"
            id="ctaLabel"
            bind:value={heroCtaText}
            size="sm"
          />
        </div>

        <Button
          onclick={saveHeroConfig}
          variant="primary"
          class="w-full mt-2 font-bold"
        >
          Save Hero configuration
        </Button>
      </div>
    </Card>

    <!-- FAQ accordion manager -->
    <Card title="Accordion FAQs">
      {#snippet actions()}
        <Button
          onclick={addFaq}
          variant="outline"
          secondary
          size="xs"
          class="border-secondary text-secondary hover:bg-secondary hover:text-white font-bold"
        >
          + Add FAQ Item
        </Button>
      {/snippet}

      <div class="space-y-4 max-h-[360px] overflow-y-auto pr-2">
        {#each faqs as faq, index}
          <Collapse title={faq.question || `FAQ #${index + 1} (New Item)`}>
            {#snippet actions()}
              <Button
                onclick={() => removeFaq(index)}
                variant="ghost"
                size="xs"
                class="text-error hover:bg-error/10"
              >
                ✕ Remove
              </Button>
            {/snippet}

            <div class="form-control w-full">
              <label for="faqQ-{index}" class="label">
                <span class="label-text text-[0.625rem] font-bold text-base-content/50 uppercase tracking-wider">Question</span>
              </label>
              <Input
                type="text"
                id="faqQ-{index}"
                bind:value={faq.question}
                size="sm"
                class="bg-base-100"
              />
            </div>
            <div class="form-control w-full">
              <label for="faqA-{index}" class="label">
                <span class="label-text text-[0.625rem] font-bold text-base-content/50 uppercase tracking-wider">Answer</span>
              </label>
              <Textarea
                id="faqA-{index}"
                rows={2}
                bind:value={faq.answer}
                class="bg-base-100"
              />
            </div>
          </Collapse>
        {/each}
      </div>

      <Button
        onclick={saveFaqConfig}
        variant="primary"
        class="w-full mt-2 font-bold"
      >
        Save FAQ accordion
      </Button>
    </Card>
  </div>
</div>
